/**
 * Centre Board Marker Module
 * 
 * This module manages the centre board marker, which is critical for
 * correctly positioning visual elements relative to the board's centre.
 *
 * The marker MUST be stable for the whole session: every mesh is placed
 * at `boardCoord + marker`, so a moving marker teleports the rendered
 * world out from under the camera. The server pins it at (0,0); the
 * fallbacks below therefore also use (0,0) — never a bounds-derived
 * midpoint, which jumps wildly when a remote battle arena inflates the
 * board bounds.
 */

const DEFAULT_CENTRE_MARKER = Object.freeze({ x: 0, z: 0 });

/**
 * Find the board centre marker in the game state
 * 
 * @param {Object} gameState - The game state object
 * @returns {Object|null} - The centre marker {x, z} or null if not found
 */
export function findBoardCentreMarker(gameState) {
	// First check for direct reference in board
	if (gameState.board && gameState.board.centreMarker &&
		typeof gameState.board.centreMarker.x === 'number' &&
		typeof gameState.board.centreMarker.z === 'number') {
		return gameState.board.centreMarker;
	}
	
	// No direct reference, check all cells
	if (gameState.board && gameState.board.cells) {
		for (const key in gameState.board.cells) {
			const cell = gameState.board.cells[key];
			
			// Handle array format (new)
			if (Array.isArray(cell)) {
				const markerData = cell.find(item => 
					(item.type === 'specialMarker' && item.isCentreMarker) ||
					(item.type === 'boardCentre'));
					
				if (markerData) {
					const [x, z] = key.split(',').map(Number);
					return { x, z };
				}
			}
			// Handle object format (legacy)
			else if (cell && cell.specialMarker && 
				(cell.specialMarker.type === 'boardCentre' || cell.specialMarker.isCentreMarker)) {
				const [x, z] = key.split(',').map(Number);
				return { x, z };
			}
		}
	}
	
	// No marker anywhere: use the fixed default. Do NOT derive a centre
	// from board bounds — bounds move (e.g. when a battle arena spawns
	// thousands of cells away) and the render origin must not.
	return { ...DEFAULT_CENTRE_MARKER };
}

/**
 * Create a centre marker in the game state
 * 
 * @param {Object} gameState - The game state to modify
 * @param {number} x - X coordinate for the centre marker
 * @param {number} z - Z coordinate for the centre marker
 * @returns {Object} The created centre marker {x, z}
 */
export function createCentreMarker(gameState, x, z) {
	// Ensure we have valid coordinates
	if (x === undefined || z === undefined) {
		const marker = findBoardCentreMarker(gameState);
		x = marker.x;
		z = marker.z;
	}
	
	console.log(`Creating centre marker at (${x}, ${z})`);
	
	// Add direct reference
	if (gameState.board) {
		gameState.board.centreMarker = { x, z };
		
		// Add to cells
		if (gameState.board.cells) {
			const key = `${x},${z}`;
			
			if (!gameState.board.cells[key]) {
				// Create new cell with special marker
				gameState.board.cells[key] = [{
					type: 'specialMarker',
					isCentreMarker: true,
					centreX: x,
					centreZ: z
				}];
			} else if (Array.isArray(gameState.board.cells[key])) {
				// Check if marker already exists in array
				const markerExists = gameState.board.cells[key].some(item => 
					(item.type === 'specialMarker' && item.isCentreMarker) ||
					(item.type === 'boardCentre'));
					
				if (!markerExists) {
					// Add to existing array
					gameState.board.cells[key].push({
						type: 'specialMarker',
						isCentreMarker: true,
						centreX: x,
						centreZ: z
					});
				}
			} else if (typeof gameState.board.cells[key] === 'object') {
				// Legacy format - add special marker property
				gameState.board.cells[key].specialMarker = {
					type: 'boardCentre',
					isCentreMarker: true,
					centreX: x,
					centreZ: z
				};
			}
		}
	}
	
	return { x, z };
}

/**
 * Preserve the centre marker from one game state to another
 * 
 * @param {Object} gameState - The current game state
 * @param {Object} newBoardData - The new board data
 * @returns {Object} The preserved centre marker {x, z}
 */
export function preserveCentreMarker(gameState, newBoardData) {
	// Find the centre marker in the current game state
	const marker = findBoardCentreMarker(gameState);
	
	// Check if the new board data already has a centre marker
	if (newBoardData.centreMarker) {
		// Only worth a log when the reference point actually moves —
		// which should be never once the server pins it at (0,0).
		if (marker && (marker.x !== newBoardData.centreMarker.x || marker.z !== newBoardData.centreMarker.z)) {
			console.warn(`Centre marker moved from (${marker.x}, ${marker.z}) to (${newBoardData.centreMarker.x}, ${newBoardData.centreMarker.z}) — re-anchoring render space`);
		}
		return newBoardData.centreMarker;
	}
	
	// If not, carry the current marker over. Note: only the board-level
	// property — we deliberately do NOT plant a marker item in `cells`
	// any more, because a cell whose only content is the marker renders
	// as a phantom floating tile (visible in open sea at (0,0)).
	if (marker) {
		newBoardData.centreMarker = { x: marker.x, z: marker.z };
		return marker;
	}
	
	// If no marker exists anywhere, anchor at the fixed default — never
	// at a bounds midpoint (bounds move; the render origin must not).
	const { x, z } = DEFAULT_CENTRE_MARKER;
	newBoardData.centreMarker = { x, z };
	return { x, z };
}

/**
 * Create a material for the centre marker
 * @param {Object} THREE - THREE.js library 
 * @returns {Object} THREE.js material
 */
export function createCentreMarkerMaterial(THREE) {
	return new THREE.MeshStandardMaterial({
		color: 0x00FF00, // Bright green for visibility
		roughness: 0.5,
		metalness: 0.5,
		transparent: true,
		opacity: 0.7 
	});
}

/**
 * Update a cell while preserving the centre marker
 * This is useful when updating cells through incremental updates.
 * 
 * @param {Object} currentCell - The existing cell data
 * @param {any} newValue - The new value to set
 * @param {Object} marker - The centre marker object
 * @returns {any} The modified new value that preserves the marker
 */
export function updateCellPreservingMarker(currentCell, newValue, marker) {
	// Early return if new value is null or undefined - we don't allow centre marker removal
	if (newValue === null || newValue === undefined) {
		console.warn('Protected centre marker from being removed');
		return currentCell; // Keep existing cell
	}
	
	// Handle array format
	if (Array.isArray(currentCell)) {
		// Find any existing marker in the array
		const markerIndex = currentCell.findIndex(item => 
			(item.type === 'specialMarker' && item.isCentreMarker) ||
			(item.type === 'boardCentre'));
			
		// If marker exists in current cell
		if (markerIndex >= 0) {
			const markerData = currentCell[markerIndex];
			
			// If new value is also an array, make sure it has the marker
			if (Array.isArray(newValue)) {
				// Check if the marker already exists in the new value
				if (!newValue.some(item => 
					(item.type === 'specialMarker' && item.isCentreMarker) ||
					(item.type === 'boardCentre'))) {
					// Add marker to new array
					newValue.push(markerData);
					console.log('Added marker to new array value');
				}
				return newValue;
			}
			// Otherwise create a new array containing the new value and the marker
			else {
				return [newValue, markerData];
			}
		}
		// No marker in current cell array, just return the new value
		else {
			return newValue;
		}
	}
	// Handle object format (legacy)
	else if (typeof currentCell === 'object' && currentCell.specialMarker) {
		// If new value is also an object, add the marker to it
		if (typeof newValue === 'object' && !Array.isArray(newValue)) {
			newValue.specialMarker = currentCell.specialMarker;
			return newValue;
		}
		// Otherwise return the current cell but update any other properties
		else {
			// If new value is simple, we can't set properties on it
			// So we keep the existing cell
			console.warn('Cannot replace object cell with non-object value at centre marker position');
			return currentCell;
		}
	}
	
	// Default - if no marker or incompatible formats, just return the new value
	return newValue;
}

/**
 * Convert absolute board coordinates to coordinates relative to the reference point
 * 
 * @param {Object} position - Position with x, z or x, y, z coordinates
 * @param {Object} referencePoint - The reference point {x, z} to calculate relative to
 * @returns {Object} Position relative to reference point
 */
export function toRelativePosition(position, referencePoint) {
	const result = {};
	
	if (position.x !== undefined && referencePoint.x !== undefined) {
		result.x = position.x - referencePoint.x;
	}
	
	// Handle either z or y coordinates depending on what's available
	if (position.z !== undefined && referencePoint.z !== undefined) {
		result.z = position.z - referencePoint.z;
	} else if (position.y !== undefined && referencePoint.z !== undefined) {
		result.y = position.y - referencePoint.z;
	}
	
	// Copy any additional properties like y-height if they exist
	if (position.y !== undefined && !result.y) {
		result.y = position.y;
	}
	
	return result;
}

/**
 * Convert relative coordinates to absolute board coordinates
 * 
 * @param {Object} relPosition - Relative position with x, z or x, y, z coordinates
 * @param {Object} referencePoint - The reference point {x, z} to calculate absolute from
 * @returns {Object} Absolute position
 */
export function toAbsolutePosition(relPosition, referencePoint) {
	const result = {};
	
	if (relPosition.x !== undefined && referencePoint.x !== undefined) {
		result.x = relPosition.x + referencePoint.x;
	}
	
	// Handle either z or y coordinates depending on what's available
	if (relPosition.z !== undefined && referencePoint.z !== undefined) {
		result.z = relPosition.z + referencePoint.z;
	} else if (relPosition.y !== undefined && referencePoint.z !== undefined) {
		// Map y to z if there's no z but the reference has z
		result.z = relPosition.y + referencePoint.z;
	}
	
	// Copy any additional properties like y-height if they exist
	if (relPosition.y !== undefined && !result.y) {
		result.y = relPosition.y;
	}
	
	return result;
}

/**
 * Translate a position using the reference point from the game state
 * 
 * @param {Object} position - Position with x, z or x, y, z coordinates
 * @param {Object} gameState - The game state containing a reference point
 * @param {boolean} toAbsolute - True to convert from relative to absolute, false for opposite
 * @returns {Object} Translated position
 */
export function translatePosition(position, gameState, toAbsolute = true) {
	const referencePoint = findBoardCentreMarker(gameState);
	
	if (!referencePoint) {
		console.warn('No reference point found for translation, returning original position');
		return position;
	}
	
	return toAbsolute ? 
		toAbsolutePosition(position, referencePoint) : 
		toRelativePosition(position, referencePoint);
}

// Export everything as a module
export default {
	findBoardCentreMarker,
	createCentreMarker,
	preserveCentreMarker,
	createCentreMarkerMaterial,
	updateCellPreservingMarker,
	toRelativePosition,
	toAbsolutePosition,
	translatePosition
}; 